<script lang="ts">
  import { showPricingModal, subscriptionStore, upgradePromptContext } from '$lib/stores/subscription';
  import { PRICING_PLANS } from '$lib/constants/subscription';
  import StatusMessage from '../StatusMessage.svelte';
  import { onMount, onDestroy } from 'svelte';
  import { useTimeout } from '$lib/hooks/useTimer';
  import { useError } from '$lib/hooks/useError';
  import { errorService } from '$lib/services/ErrorService';
  import { LicenseFormattingService } from '$lib/services/subscription/LicenseFormattingService';
  
  let billingCycle: 'monthly' | 'yearly' = 'yearly';
  let selectedPlan = 'premium_yearly';
  let isProcessing = false;
  let licenseKey = '';
  let showLicenseInput = false;
  let licenseError = '';
  
  // Status message for notifications
  let statusMessage = '';
  let statusType: 'success' | 'error' | 'info' | 'warning' = 'info';
  
  // Error handling setup
  const { tryExecute } = useError('PricingModal');
  
  // License key input handler
  function handleLicenseKeyInput(event: Event) {
    const input = event.target as HTMLInputElement;

    // Remove any non-ASCII characters (including Korean) and only keep alphanumeric and hyphens
    const filtered = input.value
      .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
      .replace(/[^A-Za-z0-9-]/g, ''); // Remove anything that's not alphanumeric or hyphen

    // Format the input using the centralized service
    licenseKey = LicenseFormattingService.formatLicenseKeyInput(filtered);

    // Update the input value directly to ensure consistency
    if (input.value !== licenseKey) {
      input.value = licenseKey;
    }
  }

  function handleLicensePaste(event: ClipboardEvent) {
    event.preventDefault();

    // Get pasted text and trim whitespace
    const pastedText = (event.clipboardData?.getData('text') || '').trim();

    // Clean and format the pasted text
    const cleaned = pastedText
      .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
      .replace(/[^A-Za-z0-9-]/g, ''); // Remove anything that's not alphanumeric or hyphen

    // Format the license key using the centralized service
    licenseKey = LicenseFormattingService.formatLicenseKeyInput(cleaned);
  }
  
  // Organize plans by billing cycle
  $: currentPlans = billingCycle === 'monthly' 
    ? [PRICING_PLANS.FREE, PRICING_PLANS.PREMIUM_MONTHLY]
    : [PRICING_PLANS.FREE, PRICING_PLANS.PREMIUM_YEARLY];
  
  $: if (billingCycle === 'monthly') {
    selectedPlan = selectedPlan === 'premium_yearly' ? 'premium_monthly' : selectedPlan;
  } else {
    selectedPlan = selectedPlan === 'premium_monthly' ? 'premium_yearly' : selectedPlan;
  }
  
  function close() {
    showPricingModal.set(false);
    showLicenseInput = false;
    licenseKey = '';
    licenseError = '';
    statusMessage = '';
  }
  
  // Auto-clear timer for status messages
  const statusClearTimer = useTimeout(() => {
    statusMessage = '';
  }, 3500, false);
  
  // Timer for closing modal after successful activation
  const closeModalTimer = useTimeout(() => {
    close();
  }, 1500, false);
  
  function showStatus(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') {
    statusMessage = message;
    statusType = type;
    
    // Auto-clear after 3.5 seconds
    statusClearTimer.restart();
  }
  
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      close();
    }
  }
  
  async function handlePurchase(planId: string) {
    if (planId === 'free') return;
    
    const [result, error] = await tryExecute(
      () => {
        // Open checkout in system browser
        openGumroadCheckout(planId);
        
        // Close modal immediately after opening browser
        close();
      },
      {
        severity: 'error',
        context: { action: 'openCheckout', planId },
        showToUser: false // We handle our own display
      }
    );
    
    if (error) {
      errorService.showWarning('구매 페이지 열기에 실패했습니다. 다시 시도해주세요.');
      showStatus('구매 페이지 열기에 실패했습니다.', 'error');
    }
  }
  
  async function handleLicenseActivation() {
    if (!licenseKey.trim()) {
      errorService.handleValidationError('licenseKey', '라이선스 키를 입력해주세요.');
      showStatus('라이선스 키를 입력해주세요.', 'error');
      return;
    }
    
    isProcessing = true;
    licenseError = '';
    
    const [success, error] = await tryExecute(
      async () => {
        const result = await subscriptionStore.activateLicense(licenseKey);
        
        if (result) {
          errorService.showSuccess('라이선스가 성공적으로 활성화되었습니다!');
          showStatus('라이선스가 성공적으로 활성화되었습니다!', 'success');
          // Use timeout hook for cleanup
          closeModalTimer.restart();
        } else {
          throw new Error('유효하지 않은 라이선스 키입니다.');
        }
        
        return result;
      },
      {
        severity: 'error',
        context: { action: 'activateLicense' },
        showToUser: false // We handle our own display
      }
    );
    
    if (error) {
      const message = error.message || '라이선스 활성화 중 오류가 발생했습니다.';
      errorService.handleApiError(error, { component: 'PricingModal', action: 'activateLicense' });
      showStatus(message, 'error');
    }
    
    isProcessing = false;
  }
  
  function formatPrice(price: number): string {
    if (price === 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  }
  
  function getAnnualSavings(): number {
    const monthlyTotal = PRICING_PLANS.PREMIUM_MONTHLY.price * 12;
    const yearlyPrice = PRICING_PLANS.PREMIUM_YEARLY.price;
    return Math.round(((monthlyTotal - yearlyPrice) / monthlyTotal) * 100);
  }
  
  function getMonthlyPrice(yearlyPrice: number): string {
    const monthly = Math.round(yearlyPrice / 12);
    return formatPrice(monthly);
  }
  
  $: contextMessage = null;
  
  // No need for Gumroad script in Electron - using external browser
  
  // Gumroad checkout URL
  const GUMROAD_CHECKOUT_URL = 'https://checkout.junlim.org';
  
  function openGumroadCheckout(planId: string) {
    // Open in system browser via Electron's shell.openExternal
    // This provides the best experience for desktop users
    window.open(GUMROAD_CHECKOUT_URL, '_blank');
  }
</script>

{#if $showPricingModal}
  <!-- Overlay -->
  <div 
    class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-6"
    role="dialog"
    aria-modal="true"
    aria-labelledby="pricing-modal-title"
    tabindex="-1"
    on:click={close}
    on:keydown={handleKeydown}
  >
    <!-- Modal - Fixed size -->
    <div 
      class="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-3xl h-[650px] flex flex-col shadow-2xl"
      role="presentation"
      on:click|stopPropagation
      on:keydown|stopPropagation
    >
      <!-- Status Message -->
      <StatusMessage 
        message={statusMessage} 
        type={statusType}
        onClose={() => statusMessage = ''}
      />
      <!-- Header - Compact but clear -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 id="pricing-modal-title" class="text-xl font-bold text-gray-900 dark:text-white">요금제 선택</h2>
        <button 
          class="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          aria-label="닫기"
          on:click={close}
        >
          <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-6 py-5">
        <!-- Billing Toggle - Prominent -->
        <div class="flex justify-center mb-6">
          <div class="inline-flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <button
              class="px-6 py-2 rounded-lg text-sm font-semibold transition-all
                {billingCycle === 'monthly' 
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-md' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}"
              on:click={() => billingCycle = 'monthly'}
            >
              월간
            </button>
            <button
              class="px-6 py-2 rounded-lg text-sm font-semibold transition-all
                {billingCycle === 'yearly' 
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-md' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}"
              on:click={() => billingCycle = 'yearly'}
            >
              연간
            </button>
          </div>
        </div>

        <!-- Context Message if any -->
        {#if contextMessage}
          <div class="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
            <p class="text-sm text-amber-800 dark:text-amber-200 text-center font-medium">{contextMessage}</p>
          </div>
        {/if}

        <!-- Pricing Cards - Larger with more details -->
        <div class="grid md:grid-cols-2 gap-6">
          {#each currentPlans as plan}
            <div
              role="button"
              tabindex="0"
              class="relative border-2 rounded-xl p-5 pt-7 cursor-pointer transition-all transform hover:scale-[1.02] w-full text-left flex flex-col min-h-[360px]
                {selectedPlan === plan.id 
                  ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 shadow-lg' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}
                {plan.id === 'free' ? 'opacity-85' : ''}"
              on:click={() => selectedPlan = plan.id}
              on:keydown={(e) => e.key === 'Enter' || e.key === ' ' ? selectedPlan = plan.id : null}
            >
              <!-- Popular Badge for Yearly with discount -->
              {#if plan.id === 'premium_yearly'}
                <div class="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span class="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold rounded-full shadow-lg whitespace-nowrap">
                    20% 할인
                  </span>
                </div>
              {/if}
              
              <!-- Current Plan Badge for Free -->
              {#if plan.id === 'free'}
                <div class="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span class="px-4 py-1.5 bg-gray-500 text-white text-xs font-bold rounded-full shadow-lg whitespace-nowrap">
                    현재 플랜
                  </span>
                </div>
              {/if}

              <!-- Content Wrapper -->
              <div class="flex-grow">
                <!-- Plan Header -->
                <div class="text-center mb-4">
                  <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <div class="flex items-baseline justify-center gap-1">
                    {#if plan.price === 0}
                      <span class="text-4xl font-bold text-gray-900 dark:text-white">$0</span>
                    {:else}
                      <span class="text-4xl font-bold text-gray-900 dark:text-white">
                        {formatPrice(plan.price)}
                      </span>
                      <span class="text-gray-500 dark:text-gray-400 text-sm ml-1">
                        /{plan.interval === 'monthly' ? '월' : '년'}
                      </span>
                    {/if}
                  </div>
                </div>

                <!-- Features List - More detailed -->
                <ul class="space-y-1.5 mb-3">
                {#each plan.features as feature}
                  <li class="flex items-start gap-2">
                    <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                {/each}

                <!-- Additional Features -->
                {#if plan.id !== 'free'}
                  <li class="flex items-start gap-2">
                    <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    <div class="text-sm text-gray-700 dark:text-gray-300">
                      <div class="font-semibold">OpenRouter AI 프록시</div>
                      <div class="text-xs text-gray-500 mt-0.5">GPT OSS 120B, Qwen2.5 32B - 최대 {plan.limits.dailyAIAnalysisLimit}회/일</div>
                    </div>
                  </li>
                {/if}

                <!-- BYOK - Available for all tiers -->
                <li class="flex items-start gap-2">
                  <svg class="w-5 h-5 {plan.id === 'free' ? 'text-blue-500' : 'text-green-500'} flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <div class="text-sm text-gray-700 dark:text-gray-300">
                    <div class="font-semibold">본인 API 키 사용 가능</div>
                    <div class="text-xs text-gray-500 mt-0.5">GPT-4, Claude 등 무제한 사용</div>
                  </div>
                </li>
                </ul>
              </div>

              <!-- Action Button -->
              {#if plan.id !== 'free'}
                <button
                  class="w-full py-3 px-4 rounded-xl font-semibold transition-all text-sm
                    {selectedPlan === plan.id 
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-md transform hover:scale-[1.02]' 
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}"
                  disabled={isProcessing}
                  on:click|stopPropagation={() => handlePurchase(plan.id)}
                >
                  {#if isProcessing && selectedPlan === plan.id}
                    처리 중...
                  {:else}
                    지금 시작하기
                  {/if}
                </button>
              {:else}
                <div class="w-full py-3 px-4 bg-gray-50 dark:bg-gray-800/50 text-center text-gray-500 dark:text-gray-400 rounded-xl text-sm font-medium">
                  현재 사용 중
                </div>
              {/if}
            </div>
          {/each}
        </div>

        <!-- Additional Info Section -->
        <div class="mt-4 bg-gray-50 dark:bg-gray-800/30 rounded-xl p-3">
          <div class="text-xs text-gray-600 dark:text-gray-400 space-y-1.5">
            <div class="flex items-start gap-2">
              <span class="text-gray-400 dark:text-gray-500">•</span>
              <span>구독은 자동으로 갱신되며, 언제든지 취소 가능합니다</span>
            </div>
            <div class="flex items-start gap-2">
              <span class="text-gray-400 dark:text-gray-500">•</span>
              <span>표시된 가격은 부가세 미포함 가격입니다</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer - Simplified -->
      <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-2 h-12">
        <div class="flex items-center h-full">
          <!-- License Key Section -->
          {#if !showLicenseInput}
            <button 
              class="text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm font-normal"
              on:click={() => showLicenseInput = true}
            >
              라이선스 키가 있으신가요?
            </button>
          {:else}
            <div class="license-input-group">
              <input
                type="text"
                placeholder="A1B2C3D4-E5F6G7H8-I9J0K1L2-M3N4O5P6"
                bind:value={licenseKey}
                class="license-input"
                disabled={isProcessing}
                maxlength="35"
                on:input={handleLicenseKeyInput}
                on:paste={handleLicensePaste}
                on:keydown={(e) => {
                  if (e.key === 'Enter') {
                    handleLicenseActivation();
                  } else if (e.key === 'Escape') {
                    showLicenseInput = false;
                    licenseKey = '';
                    licenseError = '';
                  }
                }}
              />
              <button
                class="cancel-button"
                disabled={isProcessing}
                on:click={() => {
                  showLicenseInput = false;
                  licenseKey = '';
                  licenseError = '';
                }}
              >
                취소
              </button>
              <button
                class="primary-button"
                disabled={isProcessing || !licenseKey.trim()}
                on:click={handleLicenseActivation}
              >
                {isProcessing ? '처리 중...' : '활성화'}
              </button>
            </div>
          {/if}
        </div>
        
        {#if licenseError}
          <p class="text-red-500 text-xs mt-1 absolute">{licenseError}</p>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  /* License Input Styles - Matching SubscriptionSettings */
  .license-input-group {
    display: flex;
    gap: 8px;
    flex: 1;
    max-width: 500px;
  }
  
  .license-input {
    flex: 1;
    padding: 5px 10px;
    font-size: 13px;
    font-family: 'SF Mono', Monaco, monospace;
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    background: var(--bg-primary);
    color: var(--text-primary);
    transition: all 0.2s;
  }
  
  .license-input::placeholder {
    color: var(--text-tertiary);
    opacity: 0.6;
    font-family: 'SF Mono', Monaco, monospace;
    font-size: 13px;
  }
  
  .license-input:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
  }
  
  .license-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .primary-button {
    padding: 5px 14px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    border-radius: 6px;
    background: var(--accent-primary);
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }
  
  .primary-button:hover:not(:disabled) {
    background: var(--accent-secondary);
    transform: translateY(-1px);
  }
  
  .primary-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .cancel-button {
    padding: 5px 12px;
    font-size: 13px;
    font-weight: 500;
    border: 1px solid var(--border-secondary);
    border-radius: 6px;
    background: var(--bg-secondary);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }
  
  .cancel-button:hover:not(:disabled) {
    background: var(--bg-tertiary);
    border-color: var(--border-primary);
  }
  
  .cancel-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Simple animations */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  
</style>